// src/pages/Weather.tsx — v1 (WX-01 §9, Oct 22, 2026). The weather layer at a glance, READ-ONLY.
// One call: ops_weather_overview() (ops_require('viewer')). Sections: run health · 7-day outlook per active ZIP ·
// events · NWS alerts matched to our ZIPs · trigger preview queue (rows open the panel with the full message and every
// hold) · rules and wording status. Nothing here sends or changes anything: approving rules and wording, and sending,
// come with the send step. Source: National Weather Service (api.weather.gov).
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { dateTime, shortDate, todayPacific } from "../lib/format";
import { Chip, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";
import { Panel } from "../components/Panel";

interface Run { fn: string; last_status: string | null; last_run_at: string | null; last_ok_at: string | null;
  runs_24h: number; calls_24h: number; errors_24h: number; failed_24h: number }
interface Day { date: string; rain_in: number | null; pop: number | null; gust_mph: number | null; low_f: number | null;
  high_f: number | null; snow_in: number | null; alerts: string[] }
interface Zip { zip: string; sources: string[]; wfo: string | null; points: number; days: Day[] }
interface WxEvent { id: string; type: string; severity: string; status: string; basis: string; wfo: string | null;
  starts_at: string; ends_at: string; zips: string[]; confidence: number; metrics: Record<string, unknown> }
interface Alert { event: string; severity: string | null; headline: string | null; onset: string | null; expires: string | null;
  zips: string[]; message_type: string | null; scheduling_impact: string }
interface QueueRow { id: string; rule_no: number; rule: string; channel: string; zip: string | null; to: string | null; reason: string;
  status: string; hold_reason: string | null; holds: string[] | null; campaign_id: string | null; send_after: string | null;
  message: string | null; event_status: string; created_at: string }
interface Rule { rule_no: number; name: string; events: string[]; channel: string; audience: string; lead: string; timing: string;
  service: string | null; enabled: boolean; dry_run: boolean; approved_at: string | null; template: string; template_approved: boolean }
interface Overview { generated_at: string; runs: Run[]; zips: Zip[]; events: WxEvent[]; alerts: Alert[]; queue: QueueRow[]; rules: Rule[];
  data_age: { forecast_oldest: string | null; forecast_newest: string | null; points: number; points_failing: number; properties_mapped: number } }

// cadence each job should keep (WX-01 §6); a job is "late" when its last good run is older than this
const CADENCE_MIN: Record<string, number> = { "wx-alerts-tick": 25, "wx-forecast-tick": 45, "wx-trigger-tick": 90, "wx-points-refresh": 26 * 60 };
const FN_LABEL: Record<string, string> = {
  "wx-alerts-tick": "NWS alerts (every 10 min)", "wx-forecast-tick": "Forecasts (every 15 min)",
  "wx-trigger-tick": "Trigger previews (hourly)", "wx-points-refresh": "Gridpoint mapping (daily)",
};
const HOLD_LABEL: Record<string, string> = {
  dry_run: "dry run", needs_approval: "rule not approved", wording_not_approved: "wording not approved", no_contact: "no email / phone",
  texts_not_live: "texts not live", no_consent: "no texting consent", cooldown: "cooldown", cap: "weekly cap", event_withdrawn: "event withdrawn",
};
const EVENT_LABEL = (t: string) => t.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const weekday = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
const ago = (iso: string | null) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return m < 60 ? `${m} min ago` : m < 48 * 60 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

export default function Weather() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<QueueRow | null>(null);
  const [showRules, setShowRules] = useState(false);

  const refresh = useCallback(async () => {
    const { data: d, error: e } = await supabase.rpc("ops_weather_overview");
    if (e) setError(e.message); else { setData(d as Overview); setError(""); }
  }, []);
  useEffect(() => { void refresh(); const t = setInterval(() => void refresh(), 5 * 60_000); return () => clearInterval(t); }, [refresh]);

  const today = todayPacific();
  const dates = useMemo(() => data?.zips.find((z) => z.days.length)?.days.map((d) => d.date) ?? [], [data]);

  return (
    <PageFrame
      title="Weather"
      lede="National Weather Service forecasts and alerts for every active ZIP, the events they set off, and what the trigger engine would send. Read-only: nothing on this page sends."
      panel={open && (
        <Panel kicker={`Rule ${open.rule_no} · preview`} title={open.rule} onClose={() => setOpen(null)}>
          <p className="hint">{open.reason}</p>
          <div className="chips" style={{ margin: "10px 0" }}>
            {(open.holds ?? []).map((h) => <Chip key={h} tone={h === "dry_run" ? "muted" : "warn"}>{HOLD_LABEL[h] ?? h}</Chip>)}
            {open.event_status === "withdrawn" && <Chip tone="warn">event withdrawn</Chip>}
          </div>
          <p><strong>To:</strong> {open.to ?? (open.channel === "ops_only" ? "ops" : "—")} · {open.channel}{open.zip ? ` · ${open.zip}` : ""}</p>
          <p><strong>Would send:</strong> {open.send_after ? dateTime(open.send_after) : "—"} (Pacific window)</p>
          {open.campaign_id && <p><strong>Campaign:</strong> <code>{open.campaign_id}</code></p>}
          <SectionLabel>Message</SectionLabel>
          <pre className="editor-section" style={{ whiteSpace: "pre-wrap", font: "inherit", margin: 0 }}>{open.message ?? "—"}</pre>
          <p className="hint" style={{ marginTop: 10 }}>Preview only. The wording is a draft until approved; nothing sends from here.</p>
        </Panel>
      )}
    >
      {error && <Notice kind="error">{error}</Notice>}
      {!data && !error && <Loading what="the weather layer" />}
      {data && (
        <>
          {/* ── health ── */}
          <SectionLabel>Run health</SectionLabel>
          <div className="table-scroll">
            <table className="data-table compact">
              <thead><tr><th>Job</th><th>Last run</th><th>Last OK</th><th>24 h runs</th><th>NWS calls</th><th>Errors</th><th></th></tr></thead>
              <tbody>
                {data.runs.map((r) => {
                  const late = !r.last_ok_at || Date.now() - Date.parse(r.last_ok_at) > (CADENCE_MIN[r.fn] ?? 60) * 60000;
                  return (
                    <tr key={r.fn}>
                      <td>{FN_LABEL[r.fn] ?? r.fn}</td><td>{ago(r.last_run_at)}</td><td>{ago(r.last_ok_at)}</td>
                      <td>{r.runs_24h}</td><td>{r.calls_24h}</td><td>{r.errors_24h}{r.failed_24h ? ` · ${r.failed_24h} failed run${r.failed_24h > 1 ? "s" : ""}` : ""}</td>
                      <td>{late ? <Chip tone="warn">late</Chip> : <Chip tone="info">{r.last_status ?? "ok"}</Chip>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="hint">
            {data.data_age.points} gridpoints{data.data_age.points_failing ? ` (${data.data_age.points_failing} failing)` : ""} ·{" "}
            {data.data_age.properties_mapped.toLocaleString()} properties mapped · NWS forecasts issued{" "}
            {data.data_age.forecast_oldest ? ago(data.data_age.forecast_oldest) : "—"} to {data.data_age.forecast_newest ? ago(data.data_age.forecast_newest) : "—"}
          </p>

          {/* ── outlook ── */}
          <SectionLabel>7-day outlook · rain (in) · high / low °F</SectionLabel>
          <div className="table-scroll">
            <table className="data-table compact">
              <thead><tr><th>ZIP</th>{dates.map((d) => <th key={d}>{d === today ? "Today" : `${weekday(d)} ${shortDate(d)}`}</th>)}</tr></thead>
              <tbody>
                {data.zips.map((z) => (
                  <tr key={z.zip}>
                    <td><strong>{z.zip}</strong> <span className="hint">{z.wfo ?? "unmapped"} · {z.points} pt</span></td>
                    {dates.map((d) => {
                      const day = z.days.find((x) => x.date === d);
                      if (!day) return <td key={d}>—</td>;
                      const wet = (day.rain_in ?? 0) >= 0.5;
                      return (
                        <td key={d} title={`PoP ${day.pop ?? "—"}% · gusts ${day.gust_mph ?? "—"} mph`}>
                          <span style={{ fontWeight: wet ? 700 : 400 }}>{(day.rain_in ?? 0).toFixed(2)}</span>{" "}
                          <span className="hint">{day.high_f ?? "—"}/{day.low_f ?? "—"}</span>
                          {(day.snow_in ?? 0) > 0 && <> <Chip tone="info">snow {day.snow_in}</Chip></>}
                          {day.alerts.length > 0 && <> <Chip tone="warn">{day.alerts.length} alert</Chip></>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hint">Bold = 0.5 in or more (the banner's "significant rain"). Wettest mapped gridpoint in each ZIP. Source: National Weather Service.</p>

          {/* ── events ── */}
          <SectionLabel>Events</SectionLabel>
          {data.events.length === 0 ? <p className="hint">No weather events now or in the next 8 days.</p> : (
            <div className="table-scroll">
              <table className="data-table compact">
                <thead><tr><th>Event</th><th>When (Pacific)</th><th>ZIPs</th><th>Basis</th><th>Severity</th><th>Status</th></tr></thead>
                <tbody>
                  {data.events.map((e) => (
                    <tr key={e.id}>
                      <td>{EVENT_LABEL(e.type)}{e.metrics?.peak != null ? <span className="hint"> · peak {Number(e.metrics.peak).toFixed(1)}</span> : null}</td>
                      <td>{dateTime(e.starts_at)} → {dateTime(e.ends_at)}</td>
                      <td>{e.zips.join(", ")}</td>
                      <td>{e.basis}{e.wfo ? ` · ${e.wfo}` : ""}</td>
                      <td><Chip tone={e.severity === "major" ? "warn" : "muted"}>{e.severity}</Chip></td>
                      <td><Chip tone={e.status === "active" ? "warn" : e.status === "withdrawn" ? "muted" : "info"}>{e.status}</Chip></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── alerts ── */}
          <SectionLabel>NWS alerts in our ZIPs</SectionLabel>
          {data.alerts.length === 0 ? <p className="hint">No active National Weather Service alerts touch our ZIPs.</p> : (
            <div className="table-scroll">
              <table className="data-table compact">
                <thead><tr><th>Alert</th><th>Onset → ends</th><th>ZIPs</th><th>Jobs</th></tr></thead>
                <tbody>
                  {data.alerts.map((a, i) => (
                    <tr key={i}>
                      <td><strong>{a.event}</strong>{a.headline ? <div className="hint">{a.headline}</div> : null}</td>
                      <td>{a.onset ? dateTime(a.onset) : "—"} → {a.expires ? dateTime(a.expires) : "—"}</td>
                      <td>{a.zips.join(", ")}</td>
                      <td>{a.scheduling_impact === "monitor" ? <Chip tone="warn">monitor</Chip> : <Chip tone="muted">none</Chip>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── trigger previews ── */}
          <SectionLabel>Trigger previews · would send (nothing sends)</SectionLabel>
          {data.queue.length === 0 ? (
            <p className="hint">No previews. A preview appears when an event falls inside a rule's lead time and the rule's audience has someone in those ZIPs.</p>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead><tr><th>Rule</th><th>To</th><th>ZIP</th><th>Would send</th><th>Held because</th></tr></thead>
                <tbody>
                  {data.queue.map((q) => (
                    <tr key={q.id} onClick={() => setOpen(q)} style={{ cursor: "pointer" }}>
                      <td>{q.rule_no} · {q.rule}</td>
                      <td>{q.to ?? (q.channel === "ops_only" ? "ops" : "—")} <span className="hint">{q.channel}</span></td>
                      <td>{q.zip ?? "—"}</td>
                      <td>{q.send_after ? dateTime(q.send_after) : "—"}</td>
                      <td>{q.event_status === "withdrawn" ? "event withdrawn" : (q.holds ?? []).map((h) => HOLD_LABEL[h] ?? h).join(" · ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── rules ── */}
          <SectionLabel>Rules and wording</SectionLabel>
          <p className="hint">
            {data.rules.filter((r) => r.enabled && !r.dry_run).length} of {data.rules.length} rules live ·{" "}
            {data.rules.filter((r) => r.template_approved).length} with approved wording.{" "}
            <button type="button" className="btn quiet" onClick={() => setShowRules((s) => !s)}>{showRules ? "Hide rules" : "Show rules"}</button>
          </p>
          {showRules && (
            <div className="table-scroll">
              <table className="data-table compact">
                <thead><tr><th>Rule</th><th>Events</th><th>Audience</th><th>Channel</th><th>Lead</th><th>Service</th><th>State</th></tr></thead>
                <tbody>
                  {data.rules.map((r) => (
                    <tr key={r.rule_no}>
                      <td>{r.rule_no} · {r.name}</td><td>{r.events.map(EVENT_LABEL).join(", ")}</td><td>{r.audience}</td>
                      <td>{r.channel}</td><td>{r.timing === "after" ? `after · ${r.lead}` : r.lead}</td><td>{r.service ?? "—"}</td>
                      <td>
                        {r.enabled && !r.dry_run ? <Chip tone="warn">live</Chip> : <Chip tone="muted">dry run</Chip>}{" "}
                        {r.approved_at ? <Chip tone="info">approved</Chip> : <Chip tone="muted">not approved</Chip>}{" "}
                        {r.template_approved ? <Chip tone="info">wording OK</Chip> : <Chip tone="muted">draft wording</Chip>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="hint" style={{ marginTop: 16 }}>Updated {dateTime(data.generated_at)} · refreshes every 5 minutes.</p>
        </>
      )}
    </PageFrame>
  );
}
